/**
 * Auth login visual shell — cool-blue glass atmosphere matching login-left-ui hero.
 */

import { useState } from 'react';
import { UnifiedTopbar } from '@shared/ui/layout';
import { LanguageToggle } from '@/components/i18n/LanguageToggle';

type AuthLoginShellProps = {
  children: React.ReactNode;
};

export function AuthLoginShell({ children }: AuthLoginShellProps) {
  const [loginImageError, setLoginImageError] = useState(false);

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <UnifiedTopbar isAuthenticated={false} variant="auth" topbarExtra={<LanguageToggle />} />
      <main
        className="relative flex-1 flex items-center justify-center p-6 overflow-hidden"
        style={{
          background:
            'linear-gradient(135deg, #e8eef5 0%, #dde4f0 25%, #d5ddef 50%, #e0e7f3 75%, #edf1f7 100%)',
        }}
      >
        {/* Soft ambient glow behind hero (desktop) */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 hidden lg:block"
          style={{
            background:
              'radial-gradient(ellipse 55% 70% at 28% 50%, rgba(147, 197, 253, 0.35) 0%, rgba(191, 219, 254, 0.12) 35%, transparent 70%)',
          }}
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'radial-gradient(ellipse 50% 60% at 72% 48%, rgba(255, 255, 255, 0.45) 0%, transparent 55%)',
          }}
        />

        <div className="relative z-10 w-full max-w-5xl flex items-center justify-center gap-8 lg:gap-14">
          {/* Hero — PNG already has edge fade/blur; no CSS double-mask */}
          <div className="hidden lg:flex flex-1 items-center justify-end min-h-0">
            <div
              className="relative w-full max-w-lg flex items-center justify-center"
              style={{
                animation: 'auth-hero-in 700ms ease-out both',
              }}
            >
              {loginImageError ? (
                <div className="flex items-center justify-center gap-6 opacity-40" aria-hidden>
                  <div className="w-28 h-28 rounded-full bg-primary/20" />
                  <div className="w-20 h-20 rounded-full bg-primary/15" />
                  <div className="w-36 h-36 rounded-full bg-primary/10" />
                </div>
              ) : (
                <img
                  src="/assets/login-left-ui-v2.png"
                  alt=""
                  className="w-full h-auto object-contain"
                  style={{ opacity: 0.95 }}
                  onError={() => setLoginImageError(true)}
                />
              )}
            </div>
          </div>

          {/* Frosted glass card slot */}
          <div
            className="w-full max-w-[400px] shrink-0 flex flex-col rounded-2xl border border-white/60 p-8 relative overflow-hidden"
            style={{
              background:
                'linear-gradient(160deg, rgba(255,255,255,0.72) 0%, rgba(255,255,255,0.55) 45%, rgba(248,250,252,0.48) 100%)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow:
                '0 20px 50px -20px rgba(15, 23, 42, 0.18), 0 8px 16px -8px rgba(59, 130, 246, 0.08), inset 0 1px 0 rgba(255,255,255,0.75)',
              animation: 'auth-card-in 650ms ease-out 120ms both',
            }}
          >
            {children}
          </div>
        </div>
      </main>

      <style>{`
        @keyframes auth-hero-in {
          from { opacity: 0; transform: translateX(-12px) scale(0.98); }
          to { opacity: 1; transform: translateX(0) scale(1); }
        }
        @keyframes auth-card-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

AuthLoginShell.displayName = 'AuthLoginShell';
